/**
 * Shared `rehype-sanitize` schema for every `ReactMarkdown` + `rehype-raw` call-site.
 *
 * `rehype-raw` parses raw HTML embedded in Markdown (e.g. LLM/Ask-Asper chat
 * output, task/artefact overview text, summaryText) into real DOM elements.
 * Without sanitizing the result, an attacker-controlled string such as
 * `<iframe srcdoc="<script>...</script>">` would render as a live iframe and
 * execute in the app's origin (DOM-based XSS / token theft).
 *
 * Always pair `rehypeRaw` with `[rehypeSanitize, markdownSanitizeSchema]`,
 * with sanitize listed AFTER raw in `rehypePlugins`, e.g.:
 *
 *   rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
 *
 * This starts from `hast-util-sanitize`'s GitHub-style `defaultSchema`, which
 * already drops `<iframe>`, `<script>`, `<object>`, `<embed>`, `<form>`,
 * event-handler attributes (`onerror`, `onload`, ...) and non-http(s) URLs.
 * We only add a narrow, regex-constrained `style` allowance on `<span>` so
 * the existing "colored pill" badge feature (`<span style="color:red">`)
 * keeps working, without opening up arbitrary CSS/style injection.
 */
import { defaultSchema, type Schema } from 'hast-util-sanitize';

// Matches exactly `color: <red|green|hex|rgb()>` (optionally trailing `;`),
// which is all `markdownComponents.tsx`'s badge-color detection ever needs.
// Rejects `url(...)`, multiple declarations, `!important`, etc.
const SAFE_SPAN_STYLE =
    /^\s*color\s*:\s*(red|green|#(?:[0-9a-fA-F]{3}){1,2}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\))\s*;?\s*$/i;

export const markdownSanitizeSchema: Schema = {
    ...defaultSchema,
    attributes: {
        ...defaultSchema.attributes,
        span: [...(defaultSchema.attributes?.span ?? []), ['style', SAFE_SPAN_STYLE]],
    },
};
